import configService from '@/services/config'
import leagueGroupService from '@/services/leagueGroup'
import leagueService from '@/services/league'

let configPromise = null
let configFresh = false
let leagueGroupsPromise = null
let leaguesPromise = null
let cupLeaguesPromise = null
let leagueGroupsPromiseKey = null
let leaguesPromiseKey = null
let cupLeaguesPromiseKey = null

function getSeasonPhaseKey(config) {
    if (!config || !config.activeSeasonId) return null
    return `${config.activeSeasonId}:${config.activePhase || ''}`
}

function getSeasonKey(config) {
    if (!config || !config.activeSeasonId) return null
    return `${config.activeSeasonId}`
}

export default {
    state: {
        team: localStorage.getItem('com-team') === 'null' ? null : JSON.parse(localStorage.getItem('com-team')),
        player: localStorage.getItem('com-player') === 'null' ? null : JSON.parse(localStorage.getItem('com-player')),
        config: localStorage.getItem('com-config') === 'null' ? null : JSON.parse(localStorage.getItem('com-config')),
        leagueGroups: [],
        leagueGroupsKey: null,
        leagues: [],
        leaguesKey: null,
        cupLeagues: [],
        cupLeaguesKey: null,
        leagueFilter: null,
        seasonFilter: null,
        teamFilter: null
    },
    mutations: {
        setTeam(state, value) {
            state.team = value
        },
        setPlayer(state, value) {
            state.player = value
        },
        setLeagueFilter(state, value) {
            state.leagueFilter = value
        },
        setSeasonFilter(state, value) {
            state.seasonFilter = value
        },
        setTeamFilter(state, value) {
            state.teamFilter = value
        },
        setCourtFilter(state, value) {
            state.courtFilter = value
        },
        setConfig(state, value) {
            state.config = value
        },
        setLeagueGroups(state, value) {
            state.leagueGroups = value.items
            state.leagueGroupsKey = value.key
        },
        setLeagues(state, value) {
            state.leagues = value.items
            state.leaguesKey = value.key
        },
        setCupLeagues(state, value) {
            state.cupLeagues = value.items
            state.cupLeaguesKey = value.key
        }
    },
    actions: {
        setConfig({ commit }, config) {
            localStorage.setItem('com-config', JSON.stringify(config))
            commit('setConfig', config)
        },
        // the stored config shows at once, but it is read again from the server once per page load:
        // otherwise a new active season / phase set in the CMS never reached a browser that had visited before
        ensureConfig({ state, commit }) {
            if (state.config && configFresh) {
                return Promise.resolve(state.config)
            }

            if (configPromise) {
                return configPromise
            }

            configPromise = configService.getConfig().then(resp => {
                configFresh = true
                localStorage.setItem('com-config', JSON.stringify(resp.data))
                commit('setConfig', resp.data)
                return resp.data
            }).catch(error => {
                // offline: keep going with the stored one, if any
                if (state.config) return state.config
                throw error
            }).finally(() => {
                configPromise = null
            })

            return configPromise
        },
        ensureLeagueGroups({ state, commit, dispatch }) {
            return dispatch('ensureConfig').then(config => {
                const requestKey = getSeasonPhaseKey(config)

                if (state.leagueGroups.length && state.leagueGroupsKey === requestKey) {
                    return state.leagueGroups
                }

                if (leagueGroupsPromise && leagueGroupsPromiseKey === requestKey) {
                    return leagueGroupsPromise
                }

                leagueGroupsPromiseKey = requestKey
                leagueGroupsPromise = leagueGroupService.getLeagueGroups(1, 100, { id: config.activeSeasonId }, config.activePhase).then(resp => {
                    commit('setLeagueGroups', {
                        items: resp.data.content,
                        key: requestKey
                    })
                    return resp.data.content
                }).finally(() => {
                    leagueGroupsPromise = null
                    leagueGroupsPromiseKey = null
                })

                return leagueGroupsPromise
            })
        },
        // the leagues of the menu and the home page: all the active ones, each under its league group
        // (not only those with matches in the active season and phase, which left groups empty)
        ensureLeagues({ state, commit }) {
            const requestKey = 'active'
            if (state.leagues.length && state.leaguesKey === requestKey) {
                return Promise.resolve(state.leagues)
            }
            if (leaguesPromise && leaguesPromiseKey === requestKey) {
                return leaguesPromise
            }
            leaguesPromiseKey = requestKey
            leaguesPromise = leagueService.getActiveLeagues().then(resp => {
                const items = resp.data.content || []
                commit('setLeagues', { items, key: requestKey })
                return items
            }).finally(() => {
                leaguesPromise = null
                leaguesPromiseKey = null
            })
            return leaguesPromise
        },
        ensureCupLeagues({ state, commit, dispatch }) {
            return dispatch('ensureConfig').then(config => {
                const requestKey = getSeasonKey(config)

                if (state.cupLeagues.length && state.cupLeaguesKey === requestKey) {
                    return state.cupLeagues
                }

                if (cupLeaguesPromise && cupLeaguesPromiseKey === requestKey) {
                    return cupLeaguesPromise
                }

                cupLeaguesPromiseKey = requestKey
                cupLeaguesPromise = leagueService.getCupLeagues(1, 100, { id: config.activeSeasonId }).then(resp => {
                    commit('setCupLeagues', {
                        items: resp.data.content,
                        key: requestKey
                    })
                    return resp.data.content
                }).finally(() => {
                    cupLeaguesPromise = null
                    cupLeaguesPromiseKey = null
                })

                return cupLeaguesPromise
            })
        },
        setTeam({ commit }, team) {
            localStorage.setItem('com-team', JSON.stringify(team))
            commit('setTeam', team)
        },
        setPlayer({ commit }, player) {
            localStorage.setItem('com-player', JSON.stringify(player))
            commit('setPlayer', player)
        },
        setLeagueFilter({ commit }, leagueFilter) {
            commit('setLeagueFilter', leagueFilter)
        },
        setSeasonFilter({ commit }, seasonFilter) {
            commit('setSeasonFilter', seasonFilter)
        },
        setTeamFilter({ commit }, teamFilter) {
            commit('setTeamFilter', teamFilter)
        }
    },
    getters: {
        getTeam: state => state.team,
        getPlayer: state => state.player,
        getLeagueFilter: state => state.leagueFilter,
        getSesonFilter: state => state.seasonFilter,
        getTeamFilter: state => state.teamFilter,
        getLeagueGroups: state => state.leagueGroups,
        getLeagues: state => state.leagues,
        getCupLeagues: state => state.cupLeagues,
        getConfig: state => JSON.parse(JSON.stringify(state.config))
    }
}
